import React, { FC, useMemo } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TagIcon from "@mui/icons-material/Tag";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { GkvEditBillCardProps } from "../types/interface";
import { CompactCard, CompactCardContent } from "../../GKVNew/styles/primitives";
import { NotesField } from "../styles/editPrimitives";
import { colors, radius } from "../../GKVNew/styles/tokens";
import { useDownload } from "../../../../../../hooks/useDownload";
import GkvEditBillTable from "./GkvEditBillTable";

/**
 * Card for a single bill in edit mode.
 * Design matches GKVNew GkvBillCard:
 * - Rich colored header with payer icon, name, IK, price badge
 * - Grid metadata with icon boxes
 * - Table, document links, notes
 */
const GkvEditBillCard: FC<GkvEditBillCardProps> = ({
  bill,
  selectedRxId,
  selectedRxIds,
  validationMap,
  note,
  onNoteChange,
  onRowClick,
  onMenuOpen,
  onRemoveAll,
  onTaxieren,
  onToggleRxSelection,
}) => {
  const totalPriceFormatted = useMemo(
    () =>
      Number(bill.amount || 0)
        .toFixed(2)
        .replace(".", ","),
    [bill.amount],
  );
  const billingDateStr = bill.billingDate
    ? new Date(bill.billingDate).toLocaleDateString("de-DE")
    : "–";
  const { handleDownloadPdf } = useDownload();

  return (
    <CompactCard>
      {/* Card header — matches GKVNew style */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          background: colors.payerName,
          borderBottom: `2px solid ${colors.borderLight}`,
          borderRadius: `${radius.lg} ${radius.lg} 0 0`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip title="Ganz entfernen" arrow placement="top">
            <IconButton
              size="small"
              onClick={() => onRemoveAll(bill.id)}
              sx={{
                color: colors.lightWarning,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": {
                  color: "#ff9800",
                  backgroundColor: "rgba(255, 152, 0, 0.08)",
                  boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)",
                },
              }}
            >
              <RemoveCircleOutlineIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <BusinessIcon sx={{ color: colors.accent, fontSize: 28 }} />
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              color={colors.textPrimary}
              sx={{ lineHeight: 1.2 }}
            >
              {bill.payer?.name || "Unbekannter Payer"}
            </Typography>
            <Typography variant="caption" color={colors.textPrimary} sx={{ fontSize: "0.85rem" }}>
              IK: {bill.payer?.ik || "–"}
            </Typography>
          </Box>
          {/* PDF download icons — next to payer name */}
          {bill.paperCenterReceipt?.id && (
            <Tooltip title="Urbelegbegleitschreiben herunterladen" arrow>
              <IconButton
                onClick={() => handleDownloadPdf(bill.paperCenterReceipt!.id)}
                sx={{ color: colors.headerBg }}
              >
                <HistoryEduIcon />
              </IconButton>
            </Tooltip>
          )}
          {bill.billPdf?.id && (
            <Tooltip title="Rechnung-PDF herunterladen" arrow>
              <IconButton
                onClick={() => handleDownloadPdf(bill.billPdf!.id)}
                sx={{ color: colors.headerBg }}
              >
                <RequestQuoteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              backgroundColor: colors.headerBg,
              color: "white",
              px: 2,
              py: 1,
              borderRadius: radius.md,
              textAlign: "right",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: "0.7rem", opacity: 0.9, display: "block", lineHeight: 1 }}
            >
              Gesamtpreis
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {totalPriceFormatted} €
            </Typography>
          </Box>
        </Box>
      </Box>

      <CompactCardContent>
        {/* Metadata grid — Row 1: Paper Center, Erstellt Von | Row 2: Transaction Nr., Mandant, Datum */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: bill.paperCenter ? "1fr 1fr" : "1fr",
            gap: 1.5,
            mb: 1.5,
          }}
        >
          {bill.paperCenter && (
            <MetaBox
              icon={<ReceiptIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />}
              label="Papierzentrum"
              value={bill.paperCenter.name}
            />
          )}
          <MetaBox
            icon={<PersonIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />}
            label="Erstellt von"
            value={bill.user ? `${bill.user.firstName} ${bill.user.lastName}` : "–"}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 1.5,
            mb: 2,
          }}
        >
          <MetaBox
            icon={<TagIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />}
            label="Transaction Nr."
            value={bill.transactionNumber}
          />
          <MetaBox
            icon={<AccountBalanceIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />}
            label="Mandant"
            value={bill.account?.name || "–"}
          />
          <MetaBox
            icon={<CalendarTodayIcon sx={{ color: colors.info, fontSize: 20, flexShrink: 0 }} />}
            label="Datum"
            value={billingDateStr}
          />
        </Box>

        {/* Rx Table */}
        <GkvEditBillTable
          rxs={bill.rxs ?? []}
          selectedRxId={selectedRxId}
          selectedRxIds={selectedRxIds}
          validationMap={validationMap}
          onRowClick={onRowClick}
          onMenuOpen={onMenuOpen}
          onTaxieren={onTaxieren}
          onToggleRxSelection={onToggleRxSelection}
        />

        {/* Notes */}
        <NotesField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Notizen..."
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          multiline
          minRows={1}
          maxRows={3}
          sx={{ mt: 1 }}
        />
      </CompactCardContent>
    </CompactCard>
  );
};

/** Reusable metadata info box — matches GKVNew card pattern */
const MetaBox: FC<{ icon: React.ReactNode; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      p: 1.5,
      backgroundColor: colors.infoBg,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: radius.md,
    }}
  >
    {icon}
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        color={colors.textMuted}
        sx={{ fontSize: "0.7rem", display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        color={colors.textPrimary}
        sx={{ fontSize: "0.9rem", lineHeight: 1.3, wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

export default React.memo(GkvEditBillCard);
